import { DOCUMENT } from "@angular/common";
import { DestroyRef, EnvironmentProviders, inject, Injectable, provideAppInitializer, Type } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Meta } from "@angular/platform-browser";
import { themeSwitcher as ixThemeSwitcher, ThemeVariant } from "@siemens/ix";
import { CookieService } from "ngx-cookie-service";
import { distinctUntilChanged, EMPTY, first, fromEvent, map, Observable, startWith, Subject, timer } from "rxjs";
import { fromIxTypedEvent } from "../functions/from-ix-typed-event";

type System = "system"; // Based on the OS's (or browser) theming settings
type Variant = ThemeVariant;

/** Provides and extends some extra features and control over IX's Standard Theme Handling */
@Injectable()
export class AppThemeService {
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly document = inject(DOCUMENT, { optional: true }) ?? window.document;
  protected readonly cookie = inject(CookieService);
  protected readonly meta = inject(Meta);

  /** The key used to save the user's preference */
  private readonly cookieKey = "ix-siemens-theme-variant";

  // Mozilla's spec states light themes are the default if there's no preference
  private readonly defaultTheme: Variant = "light";

  /** helper to emit changes on the themes used and related settings */
  private readonly themeChangedSubject = new Subject<void>();

  /** All valid (for the application) theme settings options */
  private readonly allThemeVariantConfigSet: ReadonlySet<unknown> = new Set<Variant | System>(["system", "dark", "light"]);

  /** Observable of the events triggered when user changes the system's color preferences */
  private readonly prefersColorSchemeDarkChange$: Observable<MediaQueryListEvent>;

  constructor() {
    this.destroyRef.onDestroy(() => this.themeChangedSubject.complete());

    const prefersColorSchemeDarkQuery = window?.matchMedia?.("(prefers-color-scheme: dark)");
    this.prefersColorSchemeDarkChange$ =
      typeof prefersColorSchemeDarkQuery?.addEventListener === "function"
        ? fromEvent<MediaQueryListEvent>(prefersColorSchemeDarkQuery, "change")
        : EMPTY;

    // Keep track of Ix Theme changes done outside this service just in case (but avoid doing so, please)
    fromIxTypedEvent(ixThemeSwitcher.themeChanged)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.themeChangedSubject.next());
  }

  /** @returns Tuple of providers for the app's bootstrapping process */
  public static getProviderList(): [Type<AppThemeService>, EnvironmentProviders] {
    return [AppThemeService, provideAppInitializer(() => inject(AppThemeService).init())];
  }

  public getSavedSettingTheme(): Variant | System {
    try {
      const theme = this.cookie.get(this.cookieKey);
      if (this.isThemeVariantSetting(theme)) {
        return theme;
      } else {
        return "system";
      }
    } catch {
      return "system";
    }
  }

  public getSavedSettingThemeVariantObservable(): Observable<Variant | System> {
    return this.themeChangedSubject.pipe(
      startWith(null),
      map(() => this.getSavedSettingTheme()),
      distinctUntilChanged()
    );
  }

  public getCurrentThemeVariant(): Variant {
    return this.document.documentElement.dataset["ixColorSchema"] === "dark" ? "dark" : "light";
  }

  public getCurrentThemeVariantObservable(): Observable<Variant> {
    return this.themeChangedSubject.pipe(
      startWith(null),
      map(() => this.getCurrentThemeVariant()),
      distinctUntilChanged()
    );
  }

  public toggleTheme(): void {
    const current = this.getCurrentThemeVariant();
    if (current === "light") {
      this.setThemeVariant("dark");
    } else {
      this.setThemeVariant("light");
    }
  }

  public setThemeVariant(theme: Variant | System): void {
    const isSystem = theme === "system";

    try {
      if (isSystem) {
        this.cookie.delete(this.cookieKey, "/");
      } else {
        // 400 days is the maximum allowed on Google Chrome
        this.cookie.set(this.cookieKey, theme, { sameSite: "Strict", secure: true, path: "/", expires: 400 });
      }
    } catch {
      console.warn("App Theme Setting couldn't be saved!");
    }

    if (isSystem) {
      ixThemeSwitcher.setColorSchema(window?.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light");
    } else {
      ixThemeSwitcher.setColorSchema(theme === "dark" ? "dark" : "light");
    }

    this.themeChangedSubject.next();

    // Update other related themes
    this.setMetaThemeColor();
    this.setBsTheme();

    // disable and re-enable transitions while the theme is being changed
    const style = this.document.createElement("style");
    style.innerHTML = `
    *,
    ::before,
    ::after {
      transition: none !important;
    }`;
    this.document.head.appendChild(style);
    timer(0)
      .pipe(first())
      .subscribe(() => style.remove());
  }

  private init(): void {
    try {
      this.setThemeVariant(this.getSavedSettingTheme());
    } catch {
      this.setThemeVariant(this.defaultTheme);
    }
    this.prefersColorSchemeDarkChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.getSavedSettingTheme() === "system") {
        this.setThemeVariant("system");
      }
    });
  }

  /** Sets the color for the browser's frame for mobile and pwa */
  private setMetaThemeColor(): void {
    const content = getComputedStyle(this.document.body).getPropertyValue("--theme-app-header--background");
    if (!content) return;
    this.meta.getTags("name='theme-color'")?.forEach((tag) => tag.remove());
    this.meta.updateTag({ name: "theme-color", content });
  }

  /**
   * Sets X-Twitter's Bootstrap theme
   * @see https://getbootstrap.com/docs/5.3/customize/color-modes/#enable-dark-mode
   */
  private setBsTheme(): void {
    const current = this.getCurrentThemeVariant();
    // Test against the opposite theme type of the default (test for light if default is dark and vice-versa)
    this.document.documentElement.dataset["bsTheme"] = current === "dark" ? "dark" : "light";
  }

  private isThemeVariantSetting(value: unknown): value is Variant | System {
    return this.allThemeVariantConfigSet.has(value);
  }
}
